export const HAIR_SHAPE = {
  root: -1,
  length: 25,
  half: 6.5,
  spread: 9.5,
  steps: 14,
  locks: 4,
  lockSteps: 4,
  lockDepth: 2.6,
  strands: 7,
  strandSpan: 0.8,
  wave: 3.2,
  waviness: 6,
} as const;

export const HAIR_POINTS = (HAIR_SHAPE.steps + 1) * 2 + HAIR_SHAPE.locks * HAIR_SHAPE.lockSteps - 1;

export function hairWave(along: number, seed: number, phase: number, size: number): number {
  return Math.sin(along * HAIR_SHAPE.waviness - phase + seed) * along * HAIR_SHAPE.wave * size;
}

export function hairSide(along: number): number {
  return HAIR_SHAPE.half + along * HAIR_SHAPE.spread;
}

export function hairMass(into: number[], length: number, phase: number, size: number): void {
  const { steps, root, locks, lockSteps, lockDepth } = HAIR_SHAPE;
  let index = 0;
  const push = (along: number, across: number): void => {
    into[index] = along;
    into[index + 1] = across;
    index += 2;
  };
  for (let step = 0; step <= steps; step += 1) {
    const u = step / steps;
    push(root - u * length, -hairSide(u) + hairWave(u, 0, phase, size));
  }
  const tip = root - length;
  const from = -hairSide(1) + hairWave(1, 0, phase, size);
  const to = hairSide(1) + hairWave(1, 0.5, phase, size);
  for (let lock = 0; lock < locks; lock += 1) {
    for (let point = 1; point <= lockSteps; point += 1) {
      if (lock === locks - 1 && point === lockSteps) {
        break;
      }
      const share = (lock + point / lockSteps) / locks;
      const bulge = Math.sin((Math.PI * point) / lockSteps) * lockDepth;
      push(tip - bulge, from + (to - from) * share);
    }
  }
  for (let back = 0; back <= steps; back += 1) {
    const u = (steps - back) / steps;
    push(root - u * length, hairSide(u) + hairWave(u, 0.5, phase, size));
  }
}

export function hairStrand(
  into: number[],
  offset: number,
  length: number,
  phase: number,
  size: number,
): void {
  const { steps, root, strandSpan } = HAIR_SHAPE;
  for (let step = 0; step <= steps; step += 1) {
    const u = step / steps;
    into[step * 2] = root - 1.5 - u * (length - 1.5);
    into[step * 2 + 1] =
      offset * strandSpan * hairSide(u) + hairWave(u, 0.25 * (offset + 1), phase, size);
  }
}
